import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useMatch, useNavigate, useOutletContext } from 'react-router-dom';
import classNames from 'classnames';
import StaticLayoutInner from '@/layouts/StaticLayoutInner';
import { fetchAPI } from '@/api';
import { OutletContext } from '@/types';
import Stripe from './Stripe';
// import Paypal from './Paypal';
import Dropdown from '@/components/Dropdown';
import ThreeDotsVertical from '@/images/icons/ThreeDotsVertical';
import { DropdownContext } from '@/components/Dropdown/DropdownContext';
import BackButtonPage from '@/components/BackButtonPage';
import ModalAction from '@/components/ModalAction';
import ErrorModal from '@/components/ErrorModal';
import LoaderIcon from '@/images/icons/Loader';
import ArrowRight from '@/images/icons/ArrowRight';

function PaymentOption(props: any) {
    const location = useLocation();
    const navigate = useNavigate();
    let match = useMatch("/settings/payment-options/:optionId");
    const { auth, project } = useOutletContext<OutletContext>();
    const { openDropdownId } = useContext(DropdownContext);
    const isDropdownOpen = openDropdownId === 'toggle-dropdown';

    const [settingModalOpen, setSettingModalOpen] = useState(false);
    const [isPaymentOptionsFetched, setIsPaymentOptionsFetched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [thanksModalOpen, setThanksModalOpen] = useState(false);
    const [thanksMessage, setThanksMessage] = useState('');
    let optionId = match?.params.optionId;

    const fetchPaymentOptions = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetchAPI("/project/payment-options", auth.user.token, {
                projectId: project.projectId,
            });

            if (!response.ok) {
                throw new Error(`This is an HTTP error: The status is ${response.status}`);
            }

            let data = await response.json();

            if (data && data.rows) {
                let option = data.rows.filter((item: any) => item.id === optionId);

                if (option && option.length > 0) {
                    setPaymentOption(option[0]);
                    navigate(location.pathname + location.search, { state: option[0], replace: true });
                }
            }
        } catch (error) {
            console.error('Error fetching payment options:', error);
        } finally {
            setIsLoading(false);
            setIsPaymentOptionsFetched(true);
        }
    }, []);

    useEffect(() => {
        fetchPaymentOptions();
    }, []);

    const [paymentOption, setPaymentOption] = useState(location.state);
    
    const getDropdownButtons = () => {
        const buttons: { text: string; onClick: () => void }[] = [];
        
        if (paymentOption?.id?.includes('stripe')) {
            const currentStripe = paymentOption.data && Object.keys(paymentOption.data).length > 0 ? paymentOption.data : null;
            
            if (paymentOption.isActive && paymentOption.isAvailable) {
                if (currentStripe) {
                    buttons.push({ text: "Settings", onClick: () => setSettingModalOpen(true)});
                }
                buttons.push({ text: "Disconnect", onClick: () => onDisconnect() });
            }
        }
        
        return buttons;
    };
    
    const onDisconnect = async () => {
        try {
            setIsActionLoading(true);
            
            const response = await fetchAPI('/project/payment-options/stripe-disconnect', auth.user.token, {
                projectId: project.projectId,
                optionId: paymentOption.id,
                liveMode: !paymentOption.isTest,
            });
            
            if (!response.ok) {
                throw new Error(`Failed to complete the operation. Please try again.`);
            }
            
            const data = await response.json();
            
            setThanksMessage(data.message || 'Operation completed successfully');
            setThanksModalOpen(true);
            setPaymentOption({
                ...paymentOption,
                data: {},
                isActive: false,
            });
            navigate(location.pathname + location.search, { state: paymentOption, replace: true });
        } catch (error: any) {
            console.error(`Error performing operation:`, error);
            setErrorMessage(error.message || `Failed to complete the operation`);
            setErrorModalOpen(true);
        } finally {
            setIsActionLoading(false);
        }
    };

    if (!optionId) {
        return <Navigate to="/settings/payment-options" />
    }

    return (
        <StaticLayoutInner>
            {!paymentOption ? (
                isLoading && (
                    <div className="flex items-center justify-center py-1 pointer-events-none mt-16 md:mt-[120px]">
                        <LoaderIcon className="animate-rotation w-10 h-10 text-brand-green" />
                    </div>
                )
            ) : (
                <div className="relative">
                    <BackButtonPage to={`/settings/payment-options`} title="Payment Options" />
                    <div className="flex flex-col w-full sm:grid sm:grid-cols-[3fr_1fr] xxl:grid-cols-[2fr_1fr] mb-8 sm:mb-12 relative">
                        <div className="bg-white border border-[#F2F2F2] rounded-[12px] py-4 md:py-4 px-4 md:px-5 flex-grow flex flex-col items-start relative">
                            <h1 className="h2 mb-4">
                                {paymentOption.text}
                                {paymentOption.isTest && <span className="bg-[#F2F2F2] text-sm font-sans font-semibold leading-[1.3] rounded-[4px] py-[3px] px-1.5 relative -top-1 ml-2">Test</span>}
                            </h1>

                            <span className="flex gap-2 mb-2">
                                {!paymentOption.isAvailable 
                                    ? <span className="badge -dot -gray-3">Unavailable</span> 
                                    : (paymentOption.isActive 
                                        ? <span className="badge -dot -green">Active</span> 
                                        : <span className="badge -dot -red">Inactive</span>
                                    )
                                }
                            </span>
                            {paymentOption.isAvailable && (
                                <div className="mt-6">
                                    {paymentOption && paymentOption.id.includes('stripe') ? (
                                        <Stripe
                                            project={project}
                                            paymentOption={paymentOption}
                                            settingModalOpen={settingModalOpen}
                                            setSettingModalOpen={setSettingModalOpen}
                                            isPaymentOptionFetched={isPaymentOptionsFetched}
                                        />
                                    ) : (
                                        <div>
                                            To manage {paymentOption.text}, please use <a href="https://t.me/InviteMemberBot" target="_blank" className="underline hover:no-underline">t.me/InviteMemberBot</a>
                                        </div>
                                    )}
                                    {/* {paymentOption.id.includes('paypal') && (
                                        <Paypal project={project} paymentOption={paymentOption} />
                                    )} */}
                                </div>
                            )}
                            {isLoading && (
                                <div className="flex items-center justify-center pointer-events-none absolute top-3 right-3">
                                    <LoaderIcon className="animate-rotation w-6 h-6 text-brand-green" />
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end gap-2.5 mt-4 sm:mt-1">
                            {getDropdownButtons().length > 0 && (
                                <Dropdown
                                    id="toggle-dropdown"
                                    button={
                                        <button className={classNames(
                                            'btn-secondary w-8 h-8 outline-0',
                                            isDropdownOpen && 'bg-[#DFD8CD] border-[#DFD8CD]',
                                        )}>
                                            <ThreeDotsVertical />
                                        </button>
                                    }
                                    className="absolute top-3 sm:top-0 right-3 sm:right-0 sm:relative inline-flex self-start"
                                    top="top-[calc(100%+10px)]"
                                    minWidth="min-w-40"
                                    >
                                    {getDropdownButtons().map((button, index) => (
                                        <button
                                            key={index}
                                            className="flex items-center py-2.5 px-4 w-full text-white text-sm font-medium border-b border-b-[#313334] last:border-b-0 first:rounded-t-xl last:rounded-b-xl hover:bg-[#313334] transition"
                                            onClick={button.onClick}
                                        >
                                            {button.text}
                                        </button>
                                    ))}
                                </Dropdown>
                            )}
                        </div>
                        {!paymentOption.isAvailable && (
                            <div className="bg-[#FFF4EE] border border-[#FFD0C2] rounded-[12px] text-sm/[1.3] text-[#E66741] p-5 mt-6">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#FF825B] w-5 h-5 mb-4">
                                    <rect width="20" height="20" rx="10" fill="currentColor"/>
                                    <path d="M7 7L13 13" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                    <path d="M13 7L7.00005 13" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>

                                <div className="font-semibold mb-2">Sorry, this payment method is currently unavailable for activation.</div>
                                <div className="mb-6">To begin accepting Live payments, please <br className="hidden sm:inline-block" /> consider subscribing to our paid plan.</div>

                                <Link to="/join" className="flex items-center gap-0.5 font-semibold">
                                Subscribe now
                                <ArrowRight className="w-5 h-5" />
                                </Link>
                            </div>
                        )}
                    </div>

                </div>
            )}

            <ModalAction
                id="thanks-modal"
                modalOpen={thanksModalOpen}
                setModalOpen={setThanksModalOpen}
                size="sm"
            >
                <h2 className="h2 mb-4 pr-16 md:pr-20 leading-[0.9]">
                    {thanksMessage}
                </h2>
                <div>
                    <button
                        className="btn-primary w-full mt-8"
                        onClick={() => setThanksModalOpen(false)}
                    >
                        {isActionLoading ? (
                            <div className="flex items-center justify-center py-1 pointer-events-none">
                                <LoaderIcon className="animate-rotation w-6 h-6 text-white" />
                            </div>
                        ) : (
                            <span className="pointer-events-none">Okay</span>
                        )}
                    </button>
                </div>
            </ModalAction>

            <ErrorModal
                modalOpen={errorModalOpen}
                setModalOpen={setErrorModalOpen}
                error={errorMessage}
            />
        </StaticLayoutInner>
    );
}

export default PaymentOption;
